import React, { useState, useEffect, useRef } from 'react';

import { useResizeObserver } from 'usehooks-ts';

import { Input, Table, Tag, InputNumber, Select, Tooltip, Flex, Button, Popconfirm, Drawer } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { NotificationInstance } from 'antd/es/notification/interface';

import { LoadingOutlined, ReloadOutlined, PlusOutlined, DeleteFilled, EditFilled, CheckCircleFilled } from '@ant-design/icons';

import IBaseComponentProps from '@components/base-component-props';

import PreloadingItems from '@flc/components/logistics/import/preloading/preloading-items';
import PreloadingItem from '@flc/components/logistics/import/preloading/preloading-item';
import PreloadingTotals from '@flc/components/logistics/import/preloading/preloading-totals';

import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';

import { exception } from '@extensions/notification';
import { userLoaded, setFilter } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { delayAction, getDate, getEnumList } from '@extensions/utils';

import { ICountry } from '@entities/country';
import { ICity } from '@entities/city';

import { IPreloadingFilter } from '@entities/preloading-filter';
import { IPreloadingLine } from '@entities/preloading-line';

import { PreloadingStatus, enumLabel as statusLabel } from '@enums/preloading-status';
import { IEnumItem } from '@enums/enum-item';

import { SumIcon } from '@icons/index';

const filterContext: string = 'NlPreloading';

interface INlPreloadingProps extends IBaseComponentProps {
    country: ICountry | undefined;
    cities: Array<ICity>;
    api: NotificationInstance;
}

const NlPreloading = (props: INlPreloadingProps) => {
    const { country, cities, api, serverFetch } = props;

    const initFilter: IPreloadingFilter = {
        statuses: [PreloadingStatus.Processed],
        countryIds: [country?.id || ''],
    };

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const d = useAppDispatch();

    const filter = useAppSelector<IPreloadingFilter>((s) => s.filters[filterContext]);

    const [lines, setLines] = useState<Array<IPreloadingLine>>();

    const [loading, setLoading] = useState<boolean>(false);
    const [showFilter, setShowFilter] = useState<boolean>(true);

    const [currentLine, setCurrentLine] = useState<IPreloadingLine>();

    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);

    const [openEditHeader, setOpenEditHeader] = useState<boolean>(false);
    const [openEditLine, setOpenEditLine] = useState<boolean>(false);
    const [openTotals, setOpenTotals] = useState<boolean>(false);

    const [statuses] = useState<Array<IEnumItem>>(
        getEnumList(PreloadingStatus, statusLabel).filter((s) => s.value != PreloadingStatus.Created),
    );

    useEffect(() => {
        if (!refreshRequired) return;

        setCurrentLine(undefined);
        setRefreshRequired(false);
        setLoading(true);

        serverFetch(`preloading/lines`, { method: 'GET', queryParams: filter })
            .then((data: Array<IPreloadingLine>) => {
                setLines(data);
                setLoading(false);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения груза', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));
            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const onDeleteLine = (id: string | undefined) => {
        if (!id) return;

        serverFetch(`preloading/lines/${id}`, { method: 'DELETE' })
            .then(() => {
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка удаления груза', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Добавить партию',
                        key: 'add',
                        disabled: loading,
                        icon: <PlusOutlined />,
                        onClick: () => {
                            setOpenEditHeader(true);
                        },
                    },
                    {
                        label: 'Итого',
                        key: 'total',
                        icon: <SumIcon style={{ fontSize: 16 }} />,
                        onClick: () => {
                            setOpenTotals(true);
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <InputNumber
                            style={{ width: 130 }}
                            key='consigneeCode'
                            placeholder='ID клиента'
                            value={filter?.consigneeCode}
                            onChange={(value) => {
                                d(setFilter({ ...filter, consigneeCode: value }, filterContext));
                            }}
                        />,
                        <Input
                            key='markingCode'
                            style={{ width: 130 }}
                            placeholder='Маркировка'
                            value={filter?.markingCode}
                            onChange={(data) => {
                                d(setFilter({ ...filter, markingCode: data.target.value }, filterContext));
                            }}
                        />,

                        <Select
                            key='city'
                            placeholder='Город доставки'
                            value={filter?.cityIds}
                            allowClear={true}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            style={{ width: 200 }}
                            showSearch
                            onChange={(value) => d(setFilter({ ...filter, cityIds: value }, filterContext))}
                            optionFilterProp='children'
                            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                            filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                            options={cities.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        />,
                        <Input
                            style={{ width: 200 }}
                            key='awbNumber'
                            placeholder='Поставщик'
                            value={filter?.awbNumber}
                            onChange={(data: React.ChangeEvent<HTMLInputElement>) => {
                                d(setFilter({ ...filter, awbNumber: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            key='status'
                            placeholder='Статус'
                            value={filter?.statuses}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 230 }}
                            onChange={(value) => d(setFilter({ ...filter, statuses: value }, filterContext))}
                            options={statuses.map((s) => {
                                return { value: s.value, label: s.label };
                            })}
                        />,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<IPreloadingLine> = [
            {
                title: '',
                width: 60,
                render: (_, record) => {
                    return (
                        <Flex>
                            <Tooltip title='Изменить груз'>
                                <Button
                                    icon={<EditFilled />}
                                    variant='link'
                                    color='blue'
                                    onClick={() => {
                                        setCurrentLine(record);
                                        setOpenEditLine(true);
                                    }}
                                />
                            </Tooltip>
                            <Popconfirm title='Удалить выбранный груз?' onConfirm={() => onDeleteLine(record.id)}>
                                <Button icon={<DeleteFilled />} variant='link' color='blue' />
                            </Popconfirm>
                        </Flex>
                    );
                },
            },
            {
                title: '',
                width: 40,
                align: 'center',
                render: (_, record) => {
                    if (record.status == PreloadingStatus.Created)
                        return (
                            <Tooltip placement='top' title='Расчет не проведен' color='var(--main-red)'>
                                <CheckCircleFilled
                                    style={{
                                        color: 'var(--main-red)',
                                        fontSize: 18,
                                        marginTop: 5,
                                    }}
                                />
                            </Tooltip>
                        );

                    return (
                        <Tooltip
                            placement='top'
                            title={record.status == PreloadingStatus.Processed ? 'Расчет проведен' : 'Отгружено'}
                            color={record.status == PreloadingStatus.Processed ? 'var(--main-green)' : '#bfbfbf'}
                        >
                            <CheckCircleFilled
                                style={{
                                    color: record.status == PreloadingStatus.Processed ? 'var(--main-green)' : '#bfbfbf',
                                    fontSize: 18,
                                    marginTop: 5,
                                }}
                            />
                        </Tooltip>
                    );
                },
            },
            {
                title: 'ID',
                align: 'center',
                width: 80,
                dataIndex: 'consigneeCode',
            },
            {
                title: 'Город',
                width: 200,
                dataIndex: 'cityName',
            },
            {
                title: 'Маркировка',
                width: 200,
                render: (_, record) => {
                    return (
                        <Tooltip placement='topLeft' title={record.markingId ? '' : 'Маркировка не найдена'} color='var(--main-red)'>
                            <div style={{ color: record.markingId ? 'var(--main-green)' : 'var(--main-red)' }}>{record.markingCode}</div>
                        </Tooltip>
                    );
                },
            },
            {
                title: 'Торговая площадка',
                align: 'center',
                dataIndex: 'awbNumber',
                width: 170,
            },
            {
                title: 'Кол-во / шт',
                align: 'center',
                dataIndex: 'qty',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    if (record.qty) return record.qty;
                    return <span style={{ color: 'var(--main-red)', fontWeight: 600 }}>0</span>;
                },
            },
            {
                title: 'Брутто / кг',
                align: 'center',
                dataIndex: 'grossWeight',
                width: 140,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    if (record.grossWeight) return record.grossWeight;

                    if (record.countryCode == 'NL') return;

                    return <span style={{ color: 'var(--main-red)', fontWeight: 600 }}>0</span>;
                },
            },
            {
                title: 'Объемный / кг',
                align: 'center',
                dataIndex: 'volumeWeight',
                width: 140,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    if (record.volumeWeight) return record.volumeWeight;

                    if (record.countryCode == 'NL') return;

                    return <span style={{ color: 'var(--main-red)', fontWeight: 600 }}>0</span>;
                },
            },
            {
                title: 'Продукт',
                width: 190,
                align: 'center',
                render: (_, record) => {
                    return (
                        <Tag
                            color={record.itemColor ?? 'red'}
                            style={{
                                marginBottom: 2,
                                paddingBottom: 2,
                                width: 'fit-content',
                                fontSize: 15,
                                fontWeight: 600,
                            }}
                        >
                            {record.itemName ?? '- Не задан -'}
                        </Tag>
                    );
                },
            },
            {
                title: 'Планируемая дата',
                width: 140,
                align: 'center',
                render: (_, record) => {
                    if (!record.plannedDate) return;

                    return (
                        <Tag variant='solid' color='var(--main-green)' style={{ fontSize: 16 }}>
                            {getDate(record.plannedDate)}
                        </Tag>
                    );
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                columns={columns}
                dataSource={lines}
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                pagination={false}
                scroll={{ y: height, x: 'max-content' }}
                summary={(pageData) => {
                    let totalQty = 0;
                    let totalGrossWeight = 0;
                    let totalVolumeWeight = 0;

                    pageData.forEach(({ qty, grossWeight, volumeWeight }) => {
                        totalQty += qty || 0;
                        totalGrossWeight += grossWeight || 0;
                        totalVolumeWeight += volumeWeight || 0;
                    });

                    return (
                        <Table.Summary fixed>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={6} align='left'>
                                    ИТОГО
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={6} align='center'>
                                    {totalQty}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={7} align='center'>
                                    {totalGrossWeight}
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={8} align='center'>
                                    {totalVolumeWeight}
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    );
                }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 295px - ${filterHeight}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {lines && renderTable()}

            {openEditHeader && (
                <PreloadingItems
                    headerId={undefined}
                    headerCountry={country}
                    baseCountries={country ? [country] : []}
                    api={api}
                    onSave={() => {
                        setOpenEditHeader(false);
                        setRefreshRequired(true);
                    }}
                    onCancel={() => {
                        setOpenEditHeader(false);
                    }}
                    serverFetch={serverFetch}
                />
            )}

            {currentLine && openEditLine && (
                <PreloadingItem
                    rootLine={currentLine}
                    api={api}
                    onSave={() => {
                        setOpenEditLine(false);
                        setRefreshRequired(true);
                    }}
                    onCancel={() => {
                        setOpenEditLine(false);
                    }}
                    serverFetch={serverFetch}
                />
            )}

            {openTotals && (
                <Drawer
                    closable
                    destroyOnHidden
                    title={`Итого`}
                    placement='right'
                    open={true}
                    onClose={() => setOpenTotals(false)}
                    size={400}
                >
                    <PreloadingTotals api={api} serverFetch={serverFetch} />
                </Drawer>
            )}
        </div>
    );
};

export default NlPreloading;
